/**
 * SectionCard — Tier 0 module-local primitive per ADR
 * `frontend-0004` (`docs/adr/frontend-0004-ui-kit-doctrine.md`). Lives
 * in this module's `_components/` folder because Tier 3 promotion to
 * `@sparta/ui-kit` requires a real cross-slice import in code, which
 * does not exist on this branch. Stays at Tier 0 until that reactive
 * trigger fires.
 *
 * Visual: a rounded-2xl bordered surface card with an optional header
 * row (title + action slot) and a body slot. The `full` flag is a
 * convenience for grid parents that want the card to span all columns
 * (`grid-column: 1 / -1`). Uses CSS variables from
 * `apps/admin/src/index.css` for surface / border colors.
 */
import * as React from 'react';

export interface SectionCardProps {
  /** Header title text. Renders the header row when provided. */
  readonly title?: string;
  /** Right-aligned slot in the header row (e.g. an action button). */
  readonly action?: React.ReactNode;
  readonly children: React.ReactNode;
  /** When true, sets `grid-column: 1 / -1` so the card spans the parent grid. */
  readonly full?: boolean;
  /** Optional id — typically wired by the enclosing scroll-anchor section. */
  readonly id?: string;
  /** Optional aria-labelledby — points at a `SectionHeading` id. */
  readonly 'aria-labelledby'?: string;
}

export function SectionCard({
  title,
  action,
  children,
  full,
  id,
  'aria-labelledby': ariaLabelledBy,
}: SectionCardProps): React.ReactElement {
  return (
    <section
      id={id}
      aria-labelledby={ariaLabelledBy}
      className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)]"
      style={full ? { gridColumn: '1 / -1' } : undefined}
    >
      {title !== undefined ? (
        <header className="flex items-center justify-between border-b border-[var(--border-subtle)] px-5 py-4">
          <h3 className="text-sm font-semibold text-[var(--fg-primary)]">
            {title}
          </h3>
          {action}
        </header>
      ) : null}
      <div className="p-5">{children}</div>
    </section>
  );
}
